/** @format */

import { BgColorsOutlined, CheckOutlined } from "@ant-design/icons";
import { Button, Popover, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { STRUCTURE_PALETTE } from "../utils/people";

interface TColors {
  background_color: string;
  text_color: string;
}

interface Props {
  value: TColors;
  onChange: (colors: TColors) => void;
  disabled?: boolean;
}

/**
 * Colori di una struttura: una palette pronta (coerente con Ant Design)
 * più la possibilità di scegliere sfondo e testo a mano.
 * I colori personalizzati vengono salvati alla chiusura del popover.
 */
export const StructureColorPicker = ({ value, onChange, disabled }: Props): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TColors>(value);

  useEffect(() => {
    if (!open) setDraft(value);
  }, [value, open]);

  const close = (commit: boolean): void => {
    setOpen(false);
    const changed =
      draft.background_color.toLowerCase() !== value.background_color.toLowerCase() ||
      draft.text_color.toLowerCase() !== value.text_color.toLowerCase();
    if (commit && changed) onChange(draft);
  };

  const pickSwatch = (colors: TColors): void => {
    setDraft(colors);
    setOpen(false);
    onChange(colors);
  };

  const content = (
    <div className="org-colors">
      <div className="org-colors__grid">
        {STRUCTURE_PALETTE.map(swatch => {
          const selected = swatch.background_color.toLowerCase() === draft.background_color.toLowerCase();
          return (
            <Tooltip key={swatch.name} title={swatch.name} mouseEnterDelay={0.3}>
              <button
                type="button"
                className={`org-colors__swatch${selected ? " is-selected" : ""}`}
                style={{
                  background: swatch.background_color,
                  color: swatch.text_color,
                  borderColor: swatch.text_color,
                }}
                onClick={(): void =>
                  pickSwatch({ background_color: swatch.background_color, text_color: swatch.text_color })
                }
                aria-label={`Palette ${swatch.name}`}
              >
                {selected ? <CheckOutlined /> : "Aa"}
              </button>
            </Tooltip>
          );
        })}
      </div>
      <div className="org-colors__custom">
        <label>
          <input
            type="color"
            value={draft.background_color}
            onChange={(e): void => setDraft({ ...draft, background_color: e.target.value })}
          />
          Sfondo
        </label>
        <label>
          <input
            type="color"
            value={draft.text_color}
            onChange={(e): void => setDraft({ ...draft, text_color: e.target.value })}
          />
          Testo
        </label>
        <span
          className="org-colors__preview"
          style={{ background: draft.background_color, color: draft.text_color, borderColor: draft.text_color }}
        >
          Anteprima
        </span>
      </div>
      <div className="org-colors__actions">
        <Button size="small" onClick={(): void => close(false)}>
          Annulla
        </Button>
        <Button size="small" type="primary" onClick={(): void => close(true)}>
          Applica
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      visible={open}
      onVisibleChange={(visible): void => (visible ? setOpen(true) : close(true))}
      placement="bottomRight"
      overlayClassName="org-colors-popover"
    >
      <Button
        type="text"
        size="small"
        disabled={disabled}
        icon={<BgColorsOutlined />}
        title="Cambia colori"
        aria-label="Cambia colori"
      />
    </Popover>
  );
};
